// src/components/workflow/WorkflowTimeline.tsx — ENHANCED VERSION
import React from 'react';
import {
    CheckCircle, XCircle, Clock, Send, Calendar, Flag, Play, Archive,
    ArrowRight, RotateCcw, UserCheck, Gavel, AlertTriangle, MessageSquare,
    ChevronRight, Timer, Ban, User, Target, Zap} from 'lucide-react';
import { LogoLoadingMark } from '../ui';
import { StateTransitionLog } from '../../types';
import { formatDistanceToNow } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

interface Props {
    history: StateTransitionLog[];
    loading: boolean;
    ppeppItems?: Array<{
        id?: number;
        created_at?: string;
        status?: string;
        pic_user?: { name: string } | null;
        pic_unit?: { name: string } | null;
        pic_user_historis?: { name: string } | null;
        pic_unit_historis?: { name: string } | null;
        target_date?: string | null;
    }> | null;
    submitTargetLabel?: string | null;
}

// ========== STATE ICONS ==========
const STATE_ICONS: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
    // Draft & Submit
    draf: { icon: <Clock className="w-4 h-4" />, color: 'text-gray-600', bgColor: 'bg-gray-100' },
    draft: { icon: <Clock className="w-4 h-4" />, color: 'text-gray-600', bgColor: 'bg-gray-100' },
    diajukan: { icon: <Send className="w-4 h-4" />, color: 'text-blue-600', bgColor: 'bg-blue-100' },
    submitted: { icon: <Send className="w-4 h-4" />, color: 'text-blue-600', bgColor: 'bg-blue-100' },

    // Validasi Level 1
    validasi_l1: { icon: <UserCheck className="w-4 h-4" />, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
    step_1_pending: { icon: <Clock className="w-4 h-4" />, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
    step_1_approved: { icon: <CheckCircle className="w-4 h-4" />, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
    step_1_rejected: { icon: <XCircle className="w-4 h-4" />, color: 'text-red-600', bgColor: 'bg-red-100' },
    ditolak_l1: { icon: <XCircle className="w-4 h-4" />, color: 'text-red-600', bgColor: 'bg-red-100' },

    // Validasi Level 2
    validasi_l2: { icon: <UserCheck className="w-4 h-4" />, color: 'text-purple-600', bgColor: 'bg-purple-100' },
    step_2_pending: { icon: <Clock className="w-4 h-4" />, color: 'text-purple-600', bgColor: 'bg-purple-100' },
    step_2_approved: { icon: <CheckCircle className="w-4 h-4" />, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
    step_2_rejected: { icon: <XCircle className="w-4 h-4" />, color: 'text-red-600', bgColor: 'bg-red-100' },
    ditolak_l2: { icon: <XCircle className="w-4 h-4" />, color: 'text-red-600', bgColor: 'bg-red-100' },

    // RAPIM
    antrean_rapim: { icon: <Calendar className="w-4 h-4" />, color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
    diputuskan: { icon: <Gavel className="w-4 h-4" />, color: 'text-purple-600', bgColor: 'bg-purple-100' },

    // Tindak Lanjut
    berlangsung: { icon: <Play className="w-4 h-4" />, color: 'text-teal-600', bgColor: 'bg-teal-100' },
    terhambat: { icon: <AlertTriangle className="w-4 h-4" />, color: 'text-red-600', bgColor: 'bg-red-100' },

    // Selesai
    selesai: { icon: <CheckCircle className="w-4 h-4" />, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
    completed: { icon: <CheckCircle className="w-4 h-4" />, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
    diarsipkan: { icon: <Archive className="w-4 h-4" />, color: 'text-gray-600', bgColor: 'bg-gray-100' },
    dibatalkan: { icon: <Ban className="w-4 h-4" />, color: 'text-red-600', bgColor: 'bg-red-100' },

    // Special
    kembali_rapim: { icon: <RotateCcw className="w-4 h-4" />, color: 'text-amber-600', bgColor: 'bg-amber-100' },
    rapim_selesai: { icon: <Zap className="w-4 h-4" />, color: 'text-teal-600', bgColor: 'bg-teal-100' }};

// ========== STATUS LABELS ==========
const STATUS_LABELS: Record<string, string> = {
    draf: 'Draft',
    draft: 'Draft',
    diajukan: 'Diajukan',
    submitted: 'Diajukan',
    validasi_l1: 'Validasi Dekan',
    step_1_pending: 'Menunggu Validasi Dekan',
    step_1_approved: 'Disetujui Dekan',
    step_1_rejected: 'Ditolak Dekan',
    ditolak_l1: 'Ditolak Dekan',
    validasi_l2: 'Validasi Wakil Rektor',
    step_2_pending: 'Menunggu Validasi Wakil Rektor',
    step_2_approved: 'Disetujui Wakil Rektor',
    step_2_rejected: 'Ditolak Wakil Rektor',
    ditolak_l2: 'Ditolak Wakil Rektor',
    antrean_rapim: 'Dalam Antrean RAPIM',
    diputuskan: 'Diputuskan RAPIM',
    berlangsung: 'Tindak Lanjut Berjalan',
    terhambat: 'Terhambat',
    selesai: 'Selesai',
    completed: 'Selesai',
    diarsipkan: 'Diarsipkan',
    dibatalkan: 'Dibatalkan'};

// ========== ROLE LABELS ==========
const ROLE_LABELS: Record<string, string> = {
    rektor: 'Rektor',
    wakil_rektor_1: 'Wakil Rektor I',
    wakil_rektor_2: 'Wakil Rektor II',
    wakil_rektor_3: 'Wakil Rektor III',
    wakil_rektor_4: 'Wakil Rektor IV',
    dekan: 'Dekan',
    wakil_dekan_1: 'Wakil Dekan I',
    wakil_dekan_2: 'Wakil Dekan II',
    wakil_dekan_3: 'Wakil Dekan III',
    kaprodi: 'Kepala Program Studi',
    sekprodi: 'Sekretaris Program Studi',
    kabag: 'Kepala Bagian',
    kasubag: 'Kepala Sub Bagian',
    ka_tu: 'Kepala Tata Usaha',
    staff: 'Staff',
    dosen: 'Dosen',
    admin: 'Administrator',
    lpm: 'LPM',
    senat: 'Senat',
    system: 'Sistem'};

// ========== SPECIAL TRANSITION DETECTION ==========
type SpecialTransitionType =
    | 'rapim_to_berlangsung'  // RAPIM selesai, tindak lanjut dimulai
    | 'submit_laporan'        // Submit pertama kali
    | 'approval_dekan'        // Disetujui Dekan
    | 'approval_warek'        // Disetujui Warek
    | 'masuk_rapim'           // Masuk antrean RAPIM
    | 'kembali_rapim'         // Dikembalikan ke RAPIM
    | 'selesai'               // Laporan selesai
    | 'reject'                // Penolakan
    | 'default';              // Default

const detectSpecialTransition = (fromState: string, toState: string): SpecialTransitionType => {
    const from = fromState?.toLowerCase() || '';
    const to = toState?.toLowerCase() || '';

    // RAPIM selesai → Tindak lanjut (gabungan diputuskan + berlangsung)
    if ((from === 'diputuskan' && to === 'berlangsung') ||
        (from === 'antrean_rapim' && to === 'diputuskan')) {
        return 'rapim_to_berlangsung';
    }

    // Submit pertama
    if (['draf', 'draft'].includes(from) && to === 'diajukan') {
        return 'submit_laporan';
    }

    // Approval Dekan
    if ((from === 'diajukan' || from === 'step_1_pending') &&
        (to === 'validasi_l2' || to === 'step_1_approved')) {
        return 'approval_dekan';
    }

    // Approval Warek
    if ((from === 'validasi_l2' || from === 'step_2_pending') &&
        (to === 'antrean_rapim' || to === 'step_2_approved')) {
        return 'approval_warek';
    }

    // Masuk RAPIM
    if (to === 'antrean_rapim' && from !== 'berlangsung' && from !== 'terhambat') {
        return 'masuk_rapim';
    }

    // Kembali ke RAPIM (dari terhambat/berlangsung)
    if (to === 'antrean_rapim' && (from === 'berlangsung' || from === 'terhambat')) {
        return 'kembali_rapim';
    }

    // Selesai
    if (to === 'selesai' || to === 'completed') {
        return 'selesai';
    }

    // Penolakan
    if (to.includes('ditolak') || to.includes('rejected')) {
        return 'reject';
    }

    return 'default';
};

// ========== HELPER FUNCTIONS ==========
const getStatusLabel = (status: string): string => {
    if (!status) return '-';
    return STATUS_LABELS[status.toLowerCase()] || formatRawStatus(status);
};

const formatRawStatus = (status: string): string => {
    if (!status) return '-';
    return status
        .replace(/_/g, ' ')
        .replace(/step (\d+)/gi, 'Tahap $1')
        .replace(/pending/gi, 'Menunggu')
        .replace(/approved/gi, 'Disetujui')
        .replace(/rejected/gi, 'Ditolak')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const getRoleLabel = (role: string): string => {
    if (!role) return '';
    return ROLE_LABELS[role.toLowerCase()] || role;
};

const isWarekRole = (role?: string | null): boolean =>
    String(role || '').toLowerCase().startsWith('wakil_rektor_');

const isSameWorkflowMoment = (left?: StateTransitionLog, right?: StateTransitionLog): boolean =>
    Boolean(
        left
        && right
        && String(left.created_at || '') === String(right.created_at || '')
        && String(left.actor_id || '') === String(right.actor_id || '')
    );

const isSystemWorkflowNote = (notes?: string | null): boolean => {
    const value = String(notes || '').trim().toLowerCase();
    if (!value) return true;

    return [
        'seluruh validasi workflow selesai',
        'disetujui melalui workflow engine',
        'otomatis masuk antrean rapim setelah semua validasi selesai',
    ].includes(value);
};

const pickDisplayNote = (items: StateTransitionLog[]): string | null => {
    for (const item of items) {
        const note = String(item.notes || '').trim();
        if (note && !isSystemWorkflowNote(note)) {
            return note;
        }
    }

    return null;
};

const getStateConfig = (state: string, specialType?: SpecialTransitionType) => {
    if (specialType === 'kembali_rapim') {
        return STATE_ICONS['kembali_rapim'];
    }
    if (specialType === 'rapim_to_berlangsung') {
        return STATE_ICONS['rapim_selesai'];
    }
    return STATE_ICONS[state?.toLowerCase()] || {
        icon: <ArrowRight className="w-4 h-4" />,
        color: 'text-gray-600',
        bgColor: 'bg-gray-100'
    };
};

const formatRelativeTime = (dateString: string): string => {
    if (!dateString) return '';
    try {
        return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: idLocale });
    } catch {
        return '';
    }
};

const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    try {
        return new Date(dateString).toLocaleString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'});
    } catch {
        return dateString;
    }
};

// ========== SPECIAL CARD RENDERERS ==========

// Card untuk transisi RAPIM → Berlangsung
const RapimSelesaiCard: React.FC<{
    item: StateTransitionLog;
    prevItem?: StateTransitionLog;
    ppeppData?: NonNullable<Props['ppeppItems']>[number] | null;
    isFirst: boolean;
}> = ({ item, prevItem, ppeppData, isFirst }) => {
    const stateConfig = getStateConfig(item.to_state, 'rapim_to_berlangsung');

    // Cari notes dari transisi antrean_rapim → diputuskan (jika ada)
    const rapimNotes = prevItem?.notes || item.notes;

    // Actor adalah yang memutuskan di RAPIM
    const deciderName = prevItem?.actor?.name || item.actor?.name || 'Pimpinan RAPIM';
    const deciderRole = getRoleLabel(prevItem?.actor_role || item.actor_role || 'rektor');
    const displayPicUser = ppeppData?.pic_user_historis || ppeppData?.pic_user;
    const displayPicUnit = ppeppData?.pic_unit_historis || ppeppData?.pic_unit;

    return (
        <div className={`
      bg-gradient-to-r from-teal-50 to-emerald-50 border-2 border-teal-200 rounded-2xl p-5 
      ${isFirst ? 'shadow-lg ring-2 ring-teal-main/20 ring-offset-2' : ''}
    `}>
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${stateConfig.bgColor} ${stateConfig.color} flex items-center justify-center`}>
                        <Zap className="w-5 h-5" />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-teal-800">
                            RAPIM Selesai — Tindak Lanjut Dimulai
                        </p>
                        <p className="text-xs text-teal-600">
                            Keputusan telah ditetapkan dalam rapat pimpinan
                        </p>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-xs text-neutral-text-secondary">{formatDate(item.created_at || '')}</p>
                    <p className="text-[10px] text-neutral-text-secondary/70">{formatRelativeTime(item.created_at || '')}</p>
                </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                {/* Pengambil Keputusan */}
                <div className="flex items-center gap-2 p-2.5 bg-white/70 rounded-xl">
                    <Gavel className="w-4 h-4 text-purple-600 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[10px] text-neutral-text-secondary uppercase tracking-wider">Keputusan oleh</p>
                        <p className="text-xs font-semibold text-neutral-text-primary truncate">{deciderName}</p>
                        <p className="text-[10px] text-neutral-text-secondary">{deciderRole}</p>
                    </div>
                </div>

                {/* PIC Ditunjuk */}
                <div className="flex items-center gap-2 p-2.5 bg-white/70 rounded-xl">
                    <User className="w-4 h-4 text-teal-600 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[10px] text-neutral-text-secondary uppercase tracking-wider">PIC Ditunjuk</p>
                        <p className="text-xs font-semibold text-neutral-text-primary truncate">
                            {displayPicUser?.name || 'Menunggu penunjukan'}
                        </p>
                        {displayPicUnit?.name && (
                            <p className="text-[10px] text-neutral-text-secondary">{displayPicUnit.name}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Target (jika ada) */}
            {ppeppData?.target_date && (
                <div className="flex items-center gap-2 p-2.5 bg-white/70 rounded-xl mb-3">
                    <Target className="w-4 h-4 text-amber-600 shrink-0" />
                    <div>
                        <p className="text-[10px] text-neutral-text-secondary uppercase tracking-wider">Target Penyelesaian</p>
                        <p className="text-xs font-semibold text-neutral-text-primary">
                            {new Date(ppeppData.target_date).toLocaleDateString('id-ID', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </p>
                    </div>
                </div>
            )}

            {/* Notes dari RAPIM */}
            {rapimNotes && (
                <div className="flex items-start gap-2 p-3 bg-white/80 rounded-xl border border-teal-200/50">
                    <MessageSquare className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />
                    <div>
                        <p className="text-[10px] text-teal-600 font-semibold uppercase tracking-wider mb-1">Catatan RAPIM</p>
                        <p className="text-xs text-neutral-text-primary leading-relaxed">{rapimNotes}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

// Card untuk Submit Laporan
const SubmitLaporanCard: React.FC<{
    item: StateTransitionLog;
    isFirst: boolean;
    targetLabel?: string | null;
}> = ({ item, isFirst, targetLabel }) => {
    const actorName = item.actor?.name || 'Pelapor';
    const actorRole = getRoleLabel(item.actor_role || '');
    const reviewerLabel = targetLabel || 'Dekan / Ketua Lembaga';

    return (
        <div className={`
      bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4
      ${isFirst ? 'shadow-md ring-2 ring-blue-main/20 ring-offset-2' : ''}
    `}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                        <Send className="w-4 h-4" />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-blue-800">Laporan Diajukan</p>
                        <p className="text-xs text-blue-600">Menunggu validasi dari {reviewerLabel}</p>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-xs text-neutral-text-secondary">{formatDate(item.created_at || '')}</p>
                    <p className="text-[10px] text-neutral-text-secondary/70">{formatRelativeTime(item.created_at || '')}</p>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">
                    {actorName.charAt(0).toUpperCase()}
                </div>
                <span className="text-xs font-medium text-neutral-text-primary">{actorName}</span>
                {actorRole && (
                    <>
                        <span className="text-neutral-text-secondary">•</span>
                        <span className="text-xs text-neutral-text-secondary">{actorRole}</span>
                    </>
                )}
            </div>

            {item.notes && (
                <div className="mt-3 p-2.5 bg-white/70 rounded-lg text-xs text-neutral-text-secondary">
                    💬 {item.notes}
                </div>
            )}
        </div>
    );
};

// Card untuk Approval (Dekan/Warek)
const ApprovalCard: React.FC<{
    item: StateTransitionLog;
    type: 'dekan' | 'warek';
    isFirst: boolean;
    noteOverride?: string | null;
}> = ({ item, type, isFirst, noteOverride }) => {
    const actorName = item.actor?.name || (type === 'dekan' ? 'Dekan' : 'Wakil Rektor');
    const actorRole = getRoleLabel(item.actor_role || '');
    const config = type === 'dekan'
        ? { gradient: 'from-indigo-50 to-purple-50', border: 'border-indigo-200', iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600', textColor: 'text-indigo-800', label: 'Disetujui Dekan', sublabel: 'Lanjut ke Wakil Rektor' }
        : { gradient: 'from-purple-50 to-pink-50', border: 'border-purple-200', iconBg: 'bg-purple-100', iconColor: 'text-purple-600', textColor: 'text-purple-800', label: 'Disetujui Wakil Rektor', sublabel: 'Masuk antrean RAPIM' };
    const displayNote = noteOverride ?? (isSystemWorkflowNote(item.notes) ? null : item.notes);

    return (
        <div className={`
      bg-gradient-to-r ${config.gradient} border ${config.border} rounded-xl p-4
      ${isFirst ? 'shadow-md ring-2 ring-emerald-main/20 ring-offset-2' : ''}
    `}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl ${config.iconBg} ${config.iconColor} flex items-center justify-center`}>
                        <CheckCircle className="w-4 h-4" />
                    </div>
                    <div>
                        <p className={`text-sm font-bold ${config.textColor}`}>{config.label}</p>
                        <p className={`text-xs ${config.iconColor}`}>{config.sublabel}</p>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-xs text-neutral-text-secondary">{formatDate(item.created_at || '')}</p>
                    <p className="text-[10px] text-neutral-text-secondary/70">{formatRelativeTime(item.created_at || '')}</p>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full ${config.iconBg} flex items-center justify-center text-xs font-bold ${config.iconColor}`}>
                    {actorName.charAt(0).toUpperCase()}
                </div>
                <span className="text-xs font-medium text-neutral-text-primary">{actorName}</span>
                {actorRole && (
                    <>
                        <span className="text-neutral-text-secondary">•</span>
                        <span className="text-xs text-neutral-text-secondary">{actorRole}</span>
                    </>
                )}
            </div>

            {displayNote && (
                <div className="mt-3 p-2.5 bg-white/70 rounded-lg text-xs text-neutral-text-secondary">
                    💬 {displayNote}
                </div>
            )}
        </div>
    );
};

// Card untuk Penolakan
const RejectCard: React.FC<{
    item: StateTransitionLog;
    isFirst: boolean;
}> = ({ item, isFirst }) => {
    const actorName = item.actor?.name || 'Validator';
    const actorRole = getRoleLabel(item.actor_role || '');
    const toLabel = getStatusLabel(item.to_state);

    return (
        <div className={`
      bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-4
      ${isFirst ? 'shadow-md ring-2 ring-red-main/20 ring-offset-2' : ''}
    `}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
                        <XCircle className="w-4 h-4" />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-red-800">{toLabel}</p>
                        <p className="text-xs text-red-600">Dikembalikan untuk revisi</p>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-xs text-neutral-text-secondary">{formatDate(item.created_at || '')}</p>
                    <p className="text-[10px] text-neutral-text-secondary/70">{formatRelativeTime(item.created_at || '')}</p>
                </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-xs font-bold text-red-600">
                    {actorName.charAt(0).toUpperCase()}
                </div>
                <span className="text-xs font-medium text-neutral-text-primary">{actorName}</span>
                {actorRole && (
                    <>
                        <span className="text-neutral-text-secondary">•</span>
                        <span className="text-xs text-neutral-text-secondary">{actorRole}</span>
                    </>
                )}
            </div>

            {item.notes && (
                <div className="p-3 bg-red-100/50 rounded-lg border border-red-200">
                    <p className="text-[10px] text-red-600 font-semibold uppercase tracking-wider mb-1">Alasan Penolakan</p>
                    <p className="text-xs text-red-800 leading-relaxed">{item.notes}</p>
                </div>
            )}
        </div>
    );
};

// Card Default
const DefaultCard: React.FC<{
    item: StateTransitionLog;
    isFirst: boolean;
}> = ({ item, isFirst }) => {
    const stateConfig = getStateConfig(item.to_state);
    const actorName = item.actor?.name || 'Sistem';
    const actorRole = getRoleLabel(item.actor_role || '');

    return (
        <div className={`
      bg-white border border-neutral-border rounded-xl p-4 transition-all duration-200
      ${isFirst ? 'shadow-md border-teal-main/20 ring-2 ring-teal-main/10 ring-offset-2' : 'hover:shadow-sm'}
    `}>
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                    {item.from_state && (
                        <>
                            <span className="text-xs px-2 py-0.5 bg-neutral-bg rounded-md text-neutral-text-secondary font-medium">
                                {getStatusLabel(item.from_state)}
                            </span>
                            <ChevronRight className="w-3.5 h-3.5 text-neutral-text-secondary shrink-0" />
                        </>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-md font-bold ${stateConfig.bgColor} ${stateConfig.color}`}>
                        {getStatusLabel(item.to_state)}
                    </span>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-xs text-neutral-text-secondary">{formatDate(item.created_at || '')}</p>
                    <p className="text-[10px] text-neutral-text-secondary/70">{formatRelativeTime(item.created_at || '')}</p>
                </div>
            </div>

            {/* Actor */}
            <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-neutral-bg flex items-center justify-center text-xs font-bold text-neutral-text-secondary">
                    {actorName.charAt(0).toUpperCase()}
                </div>
                <span className="text-xs font-medium text-neutral-text-primary">{actorName}</span>
                {actorRole && (
                    <>
                        <span className="text-neutral-text-secondary">•</span>
                        <span className="text-xs px-1.5 py-0.5 bg-teal-main/5 text-teal-main rounded font-medium text-[10px]">
                            {actorRole}
                        </span>
                    </>
                )}
            </div>

            {/* Notes */}
            {item.notes && (
                <div className="mt-2 flex items-start gap-2 text-xs p-2.5 bg-neutral-bg rounded-lg text-neutral-text-secondary">
                    <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60" />
                    <span className="leading-relaxed">{item.notes}</span>
                </div>
            )}
        </div>
    );
};

// ========== MAIN COMPONENT ==========
const WorkflowTimeline: React.FC<Props> = ({ history, loading, ppeppItems, submitTargetLabel }) => {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <LogoLoadingMark size="md" />
            </div>
        );
    }

    if (!history || history.length === 0) {
        return (
            <div className="text-center py-8">
                <Timer className="w-10 h-10 text-neutral-text-secondary opacity-30 mx-auto mb-2" />
                <p className="text-sm text-neutral-text-secondary">Belum ada riwayat perubahan status</p>
            </div>
        );
    }

    // Group consecutive RAPIM transitions
    const processedHistory: Array<{
        type: SpecialTransitionType;
        items: StateTransitionLog[];
    }> = [];

    let i = 0;
    while (i < history.length) {
        const item = history[i];
        const specialType = detectSpecialTransition(item.from_state || '', item.to_state);
        const nextItem = history[i + 1];
        const thirdItem = history[i + 2];

        const isDirectWarekToRapimSequence =
            specialType === 'approval_warek'
            && Boolean(nextItem && thirdItem)
            && nextItem?.from_state?.toLowerCase() === 'validasi_l1'
            && nextItem?.to_state?.toLowerCase() === 'validasi_l2'
            && thirdItem?.from_state?.toLowerCase() === 'step_1_pending'
            && thirdItem?.to_state?.toLowerCase() === 'step_1_approved'
            && isSameWorkflowMoment(item, nextItem)
            && isSameWorkflowMoment(item, thirdItem);

        if (isDirectWarekToRapimSequence) {
            processedHistory.push({
                type: 'approval_warek',
                items: [item, nextItem!, thirdItem!],
            });
            i += 3;
            continue;
        }

        // Check if next item is also part of RAPIM transition
        if (specialType === 'rapim_to_berlangsung' && i + 1 < history.length) {
            const nextType = detectSpecialTransition(nextItem.from_state || '', nextItem.to_state);

            if (nextType === 'rapim_to_berlangsung') {
                // Combine both transitions
                processedHistory.push({
                    type: 'rapim_to_berlangsung',
                    items: [item, nextItem]});
                i += 2;
                continue;
            }
        }

        processedHistory.push({
            type: specialType,
            items: [item]});
        i++;
    }

    const sortedPpeppItems = Array.isArray(ppeppItems)
        ? [...ppeppItems].sort((a, b) => {
            const timeA = a?.created_at ? new Date(a.created_at).getTime() : 0;
            const timeB = b?.created_at ? new Date(b.created_at).getTime() : 0;

            if (timeA === timeB) {
                return (Number(b?.id || 0) - Number(a?.id || 0));
            }

            return timeB - timeA;
        })
        : [];

    let rapimSequenceIndex = 0;

    return (
        <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-5 top-3 bottom-3 w-0.5 bg-gradient-to-b from-teal-main via-neutral-border to-neutral-bg" />

            <div className="space-y-4">
                {processedHistory.map((entry, idx) => {
                    const isFirst = idx === 0;
                    const mainItem = entry.items[0];
                    const prevItem = entry.items[1];
                    const matchedPpepp = entry.type === 'rapim_to_berlangsung'
                        ? (sortedPpeppItems[rapimSequenceIndex++] ?? null)
                        : null;

                    // Render based on transition type
                    const renderCard = () => {
                        switch (entry.type) {
                            case 'rapim_to_berlangsung':
                                return (
                                    <RapimSelesaiCard
                                        item={mainItem}
                                        prevItem={prevItem}
                                        ppeppData={matchedPpepp}
                                        isFirst={isFirst}
                                    />
                                );

                            case 'submit_laporan':
                                return <SubmitLaporanCard item={mainItem} isFirst={isFirst} targetLabel={submitTargetLabel} />;

                            case 'approval_dekan':
                                return (
                                    <ApprovalCard
                                        item={mainItem}
                                        type={isWarekRole(mainItem.actor_role) ? 'warek' : 'dekan'}
                                        isFirst={isFirst}
                                        noteOverride={pickDisplayNote(entry.items)}
                                    />
                                );

                            case 'approval_warek':
                                return (
                                    <ApprovalCard
                                        item={mainItem}
                                        type="warek"
                                        isFirst={isFirst}
                                        noteOverride={pickDisplayNote(entry.items)}
                                    />
                                );

                            case 'reject':
                                return <RejectCard item={mainItem} isFirst={isFirst} />;

                            case 'kembali_rapim':
                                return (
                                    <div className={`
                    bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4
                    ${isFirst ? 'shadow-md ring-2 ring-amber-main/20 ring-offset-2' : ''}
                  `}>
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                                                <RotateCcw className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-amber-800">Dikembalikan ke RAPIM</p>
                                                <p className="text-xs text-amber-600">Tindak lanjut mengalami hambatan</p>
                                            </div>
                                        </div>
                                        {mainItem.notes && (
                                            <div className="p-2.5 bg-white/70 rounded-lg text-xs text-amber-800">
                                                💬 {mainItem.notes}
                                            </div>
                                        )}
                                    </div>
                                );

                            case 'selesai':
                                return (
                                    <div className={`
                    bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-4
                    ${isFirst ? 'shadow-md ring-2 ring-emerald-main/20 ring-offset-2' : ''}
                  `}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                                                <CheckCircle className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-emerald-800">Laporan Selesai</p>
                                                <p className="text-xs text-emerald-600">Tindak lanjut telah diselesaikan</p>
                                            </div>
                                        </div>
                                    </div>
                                );

                            default:
                                return <DefaultCard item={mainItem} isFirst={isFirst} />;
                        }
                    };

                    return (
                        <div key={mainItem.id || idx} className="relative pl-12">
                            {/* Icon */}
                            <div className={`
                absolute left-0 w-10 h-10 rounded-xl flex items-center justify-center
                ${entry.type === 'rapim_to_berlangsung' ? 'bg-teal-100 text-teal-600' :
                                    entry.type === 'submit_laporan' ? 'bg-blue-100 text-blue-600' :
                                        entry.type === 'approval_dekan' ? 'bg-indigo-100 text-indigo-600' :
                                            entry.type === 'approval_warek' ? 'bg-purple-100 text-purple-600' :
                                                entry.type === 'reject' ? 'bg-red-100 text-red-600' :
                                                    entry.type === 'kembali_rapim' ? 'bg-amber-100 text-amber-600' :
                                                        entry.type === 'selesai' ? 'bg-emerald-100 text-emerald-600' :
                                                            'bg-gray-100 text-gray-600'
                                }
                ${isFirst ? 'ring-2 ring-offset-2 ring-teal-main/30 shadow-md' : ''}
              `}>
                                {entry.type === 'rapim_to_berlangsung' ? <Zap className="w-5 h-5" /> :
                                    entry.type === 'submit_laporan' ? <Send className="w-4 h-4" /> :
                                        entry.type === 'approval_dekan' || entry.type === 'approval_warek' ? <CheckCircle className="w-4 h-4" /> :
                                            entry.type === 'reject' ? <XCircle className="w-4 h-4" /> :
                                                entry.type === 'kembali_rapim' ? <RotateCcw className="w-4 h-4" /> :
                                                    entry.type === 'selesai' ? <Flag className="w-4 h-4" /> :
                                                        <ArrowRight className="w-4 h-4" />
                                }
                            </div>

                            {renderCard()}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WorkflowTimeline;
